import { type ClientContext, ContextClient } from '~/services/contextClient';
import type { QueryParams } from '~/services/api';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import WorkflowsClientFactory from './workflowsClient.factory';
import type {
    CreateWorkflow,
    CreateWorkflowComment,
    StopMode,
    UpdateWorkflow,
    UpdateWorkflowComment,
    WorkflowComment,
    WorkflowUsage,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import type {
    Workflow,
    WorkflowDocument,
    WorkflowDocumentInput,
    WorkflowVersionOccupancy,
    WorkflowVersionSummary,
} from '~/types/workflow.types';
import type {
    SimulationApiDestinationRequest,
    SimulationApiDestinationResponse,
    SimulationCriteriaRequest,
    SimulationCriteriaResponse,
    SimulationSegmentsRequest,
    SimulationSegmentsResponse,
    SimulationSendRequest,
    SimulationSendResponse,
} from '~/types/workflowSimulation.types';
import type { AxiosPromise } from 'axios';

class WorkflowsClient extends ContextClient<WorkflowsClientInterface> {
    constructor() {
        super(WorkflowsClientFactory);
    }

    getWorkflows(context: ClientContext): AxiosPromise<{ data: Workflow[] }> {
        return this.getClient(context).getWorkflows();
    }

    getWorkflowUsage(context: ClientContext, params: QueryParams): AxiosPromise<{ data: WorkflowUsage[] }> {
        return this.getClient(context).getWorkflowUsage(params);
    }

    getWorkflow(
        context: ClientContext,
        workflowId: number,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<Workflow> {
        return this.getClient(context).getWorkflow(workflowId, config);
    }

    createWorkflow(context: ClientContext, input: CreateWorkflow): AxiosPromise<Workflow> {
        return this.getClient(context).createWorkflow(input);
    }

    updateWorkflow(context: ClientContext, workflowId: number, input: UpdateWorkflow): AxiosPromise<Workflow> {
        return this.getClient(context).updateWorkflow(workflowId, input);
    }

    archiveWorkflow(context: ClientContext, workflowId: number, mode?: StopMode): AxiosPromise<Workflow> {
        return this.getClient(context).archiveWorkflow(workflowId, mode);
    }

    getWorkflowVersions(
        context: ClientContext,
        workflowId: number,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<{ data: WorkflowVersionSummary[] }> {
        return this.getClient(context).getWorkflowVersions(workflowId, config);
    }

    getWorkflowVersion(
        context: ClientContext,
        workflowId: number,
        version: number,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<WorkflowDocument> {
        return this.getClient(context).getWorkflowVersion(workflowId, version, config);
    }

    updateWorkflowVersion(
        context: ClientContext,
        workflowId: number,
        version: number,
        input: WorkflowDocumentInput
    ): AxiosPromise<WorkflowDocument> {
        return this.getClient(context).updateWorkflowVersion(workflowId, version, input);
    }

    publishWorkflowVersion(
        context: ClientContext,
        workflowId: number,
        version: number,
        mode?: StopMode
    ): AxiosPromise<WorkflowDocument> {
        return this.getClient(context).publishWorkflowVersion(workflowId, version, mode);
    }

    terminateWorkflowVersion(
        context: ClientContext,
        workflowId: number,
        version: number
    ): AxiosPromise<WorkflowDocument> {
        return this.getClient(context).terminateWorkflowVersion(workflowId, version);
    }

    getWorkflowOccupancy(
        context: ClientContext,
        workflowId: number
    ): AxiosPromise<{ data: WorkflowVersionOccupancy[] }> {
        return this.getClient(context).getWorkflowOccupancy(workflowId);
    }

    evaluateWorkflowSimulationCriteria(
        context: ClientContext,
        input: SimulationCriteriaRequest,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<SimulationCriteriaResponse> {
        return this.getClient(context).evaluateWorkflowSimulationCriteria(input, config);
    }

    evaluateWorkflowSimulationSegments(
        context: ClientContext,
        input: SimulationSegmentsRequest,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<SimulationSegmentsResponse> {
        return this.getClient(context).evaluateWorkflowSimulationSegments(input, config);
    }

    previewWorkflowSimulationSend(
        context: ClientContext,
        input: SimulationSendRequest,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<SimulationSendResponse> {
        return this.getClient(context).previewWorkflowSimulationSend(input, config);
    }

    previewWorkflowSimulationApiDestination(
        context: ClientContext,
        input: SimulationApiDestinationRequest,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<SimulationApiDestinationResponse> {
        return this.getClient(context).previewWorkflowSimulationApiDestination(input, config);
    }

    getWorkflowComments(context: ClientContext): AxiosPromise<{ data: WorkflowComment[] }> {
        return this.getClient(context).getWorkflowComments();
    }

    createWorkflowComment(context: ClientContext, input: CreateWorkflowComment): AxiosPromise<WorkflowComment> {
        return this.getClient(context).createWorkflowComment(input);
    }

    updateWorkflowComment(
        context: ClientContext,
        commentId: number,
        input: UpdateWorkflowComment
    ): AxiosPromise<WorkflowComment> {
        return this.getClient(context).updateWorkflowComment(commentId, input);
    }

    deleteWorkflowComment(context: ClientContext, commentId: number): AxiosPromise<void> {
        return this.getClient(context).deleteWorkflowComment(commentId);
    }
}

export default new WorkflowsClient();
